import React, { useState } from "react";
import {
  FaChevronDown,
  FaFilter,
  FaHeart,
  FaSearch,
  FaShieldAlt,
  FaTimes,
} from "react-icons/fa";

import {
  BELT_OPTIONS,
  FAMILY_OPTIONS,
  TECHNIQUE_MODES,
} from "../utils/techniquesRevisionUtils";

function SelectField({ label, value, onChange, options, placeholder }) {
  return (
    <label className="block">
      <span className="mb-2 block text-xs font-bold uppercase tracking-[0.14em] text-slate-400">
        {label}
      </span>

      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm font-bold text-slate-700 outline-none transition focus:border-sky-300 focus:ring-4 focus:ring-sky-100 dark:border-white/10 dark:bg-slate-950 dark:text-white dark:focus:border-sky-300/50 dark:focus:ring-sky-500/10"
      >
        <option value="all">{placeholder}</option>

        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    </label>
  );
}

function TechniquesToolbar({
  filters,
  uv2Fiches = [],
  resultsCount = 0,
  totalCount = 0,
  onFilterChange,
  onReset,
}) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="sticky top-[88px] z-30 rounded-[2rem] border border-slate-200 bg-white/95 p-4 shadow-xl shadow-slate-200/60 backdrop-blur-xl dark:border-white/10 dark:bg-slate-900/95 dark:shadow-black/20 lg:top-[104px]">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <p className="text-sm font-bold text-slate-950 dark:text-white">
            {resultsCount} technique{resultsCount > 1 ? "s" : ""} affichée
            {resultsCount > 1 ? "s" : ""} / {totalCount}
          </p>

          <p className="mt-1 text-xs font-medium text-slate-500 dark:text-white/45">
            Change de mode pour réviser les techniques, UV2, katas ou lancer un
            quiz.
          </p>
        </div>

        <button
          type="button"
          onClick={() => setIsOpen((prev) => !prev)}
          className="inline-flex min-h-11 items-center justify-center gap-2 rounded-2xl bg-slate-950 px-4 py-2 text-sm font-bold text-white transition hover:-translate-y-0.5 dark:bg-white dark:text-slate-950"
        >
          <FaFilter />
          Filtres
          <FaChevronDown
            className={`transition ${isOpen ? "rotate-180" : ""}`}
          />
        </button>
      </div>

      <div className="mt-4 grid gap-3 lg:grid-cols-[1fr_2fr]">
        <select
          value={filters.mode}
          onChange={(event) => onFilterChange("mode", event.target.value)}
          className="h-12 w-full rounded-2xl border border-slate-200 bg-slate-950 px-4 text-sm font-bold text-white outline-none transition focus:border-sky-300 focus:ring-4 focus:ring-sky-100 dark:border-white/10 dark:bg-white dark:text-slate-950"
        >
          {TECHNIQUE_MODES.map((mode) => (
            <option key={mode.value} value={mode.value}>
              {mode.label}
            </option>
          ))}
        </select>

        <label className="relative block">
          <FaSearch className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />

          <input
            type="search"
            value={filters.search}
            onChange={(event) => onFilterChange("search", event.target.value)}
            placeholder="Rechercher par nom, traduction, famille, ceinture..."
            className="h-12 w-full rounded-2xl border border-slate-200 bg-slate-50 pl-11 pr-4 text-sm font-bold text-slate-700 outline-none transition placeholder:text-slate-400 focus:border-sky-300 focus:bg-white focus:ring-4 focus:ring-sky-100 dark:border-white/10 dark:bg-white/[0.05] dark:text-white dark:focus:border-sky-300/50 dark:focus:bg-slate-950 dark:focus:ring-sky-500/10"
          />
        </label>
      </div>

      {isOpen && (
        <div className="mt-4 rounded-[1.5rem] border border-slate-200 bg-slate-50 p-4 dark:border-white/10 dark:bg-white/[0.03]">
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
            <SelectField
              label="Ceinture"
              value={filters.ceinture}
              onChange={(value) => onFilterChange("ceinture", value)}
              options={BELT_OPTIONS}
              placeholder="Toutes les ceintures"
            />

            <SelectField
              label="Famille"
              value={filters.famille}
              onChange={(value) => onFilterChange("famille", value)}
              options={FAMILY_OPTIONS}
              placeholder="Toutes les familles"
            />

            <label className="block">
              <span className="mb-2 block text-xs font-bold uppercase tracking-[0.14em] text-slate-400">
                Fiche UV2
              </span>

              <select
                value={filters.uv2}
                onChange={(event) => onFilterChange("uv2", event.target.value)}
                className="h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm font-bold text-slate-700 outline-none transition focus:border-sky-300 focus:ring-4 focus:ring-sky-100 dark:border-white/10 dark:bg-slate-950 dark:text-white dark:focus:border-sky-300/50 dark:focus:ring-sky-500/10"
              >
                <option value="all">Choisir une fiche</option>

                {uv2Fiches.map((fiche) => (
                  <option key={fiche} value={fiche}>
                    Fiche {fiche}
                  </option>
                ))}
              </select>
            </label>

            <label className="block">
              <span className="mb-2 block text-xs font-bold uppercase tracking-[0.14em] text-slate-400">
                Tri
              </span>

              <select
                value={filters.sort}
                onChange={(event) => onFilterChange("sort", event.target.value)}
                className="h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm font-bold text-slate-700 outline-none transition focus:border-sky-300 focus:ring-4 focus:ring-sky-100 dark:border-white/10 dark:bg-slate-950 dark:text-white dark:focus:border-sky-300/50 dark:focus:ring-sky-500/10"
              >
                <option value="nameAsc">Nom A-Z</option>
                <option value="beltAsc">Ceinture</option>
                <option value="familyAsc">Famille</option>
              </select>
            </label>

            <div className="grid gap-2">
              <label className="flex min-h-12 items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 dark:border-white/10 dark:bg-slate-950">
                <span className="inline-flex items-center gap-2 text-sm font-bold text-slate-700 dark:text-white">
                  <FaHeart className="text-red-500" />
                  Favoris
                </span>

                <input
                  type="checkbox"
                  checked={filters.favoritesOnly}
                  onChange={(event) =>
                    onFilterChange("favoritesOnly", event.target.checked)
                  }
                  className="h-5 w-5 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
                />
              </label>

              <label className="flex min-h-12 items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 dark:border-white/10 dark:bg-slate-950">
                <span className="inline-flex items-center gap-2 text-sm font-bold text-slate-700 dark:text-white">
                  <FaShieldAlt className="text-sky-600" />
                  Self-défense
                </span>

                <input
                  type="checkbox"
                  checked={filters.includeSelfDefense}
                  onChange={(event) =>
                    onFilterChange("includeSelfDefense", event.target.checked)
                  }
                  className="h-5 w-5 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
                />
              </label>
            </div>
          </div>

          <div className="mt-4 flex justify-end">
            <button
              type="button"
              onClick={onReset}
              className="inline-flex min-h-11 items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 transition hover:-translate-y-0.5 hover:border-red-200 hover:text-red-600 dark:border-white/10 dark:bg-slate-950 dark:text-white"
            >
              <FaTimes />
              Réinitialiser
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default TechniquesToolbar;
